// src/layouts/CustomLayout.tsx
import React from 'react';
import { Layout, LayoutProps } from 'react-admin';
import { useTheme, useMediaQuery, alpha } from '@mui/material';
import { Navigate, useLocation } from 'react-router-dom';
import { documentationOnlyRedirect, isDocumentationOnly } from '@/features/developers/access';
import { CustomMenu } from './CustomMenu';
import { CustomAppBar } from './CustomAppBar';
import { APP_BAR_HEIGHT, useSectionTabs } from './useSectionTabs';
import { MuiLocaleProvider } from '@/theme/MuiLocaleProvider';
import { AnalyticsPeriodProvider } from '@/providers/analyticsPeriodProvider';
import { AssistantProvider, useAssistant } from '@/features/ask/AssistantProvider';
import { ReleaseNotesPrompt } from '@/features/release-notes';
import { AssistantPanel, ASSISTANT_PANEL_WIDTH, useAssistantDocked } from '@/features/ask/components/AssistantPanel';

const LayoutFrame: React.FC<LayoutProps> = (props) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const location = useLocation();
    const isDashboard = location.pathname === '/';
    // The section bar hangs absolutely under the app bar; the content column
    // reserves its height here, from the same hook that renders it.
    const { height: sectionBarHeight } = useSectionTabs();
    const chromeHeight = APP_BAR_HEIGHT + sectionBarHeight;
    // «Preguntar» docked: the whole frame (app bar included) makes room for the panel.
    const { open: assistantOpen } = useAssistant();
    const assistantDocked = useAssistantDocked() && assistantOpen;

    return (
        <MuiLocaleProvider>
        <Layout
            {...props}
            appBar={CustomAppBar}
            menu={CustomMenu}
            sx={{
                // 1. Fondo general de la página
                '&': {
                    backgroundColor: theme.palette.background.default,
                    paddingRight: assistantDocked ? `${ASSISTANT_PANEL_WIDTH}px` : 0,
                    // react-admin's Layout root ships `min-width: fit-content`, which
                    // lets wide intrinsic content stretch the whole shell past the
                    // viewport and produce a page-level horizontal scrollbar. Wide
                    // content must scroll inside its own container, never the page body.
                    minWidth: 0,
                },

                // --- LÓGICA SOLO PARA ESCRITORIO ---
                // Fija la altura del layout a la de la pantalla y prohíbe el scroll global.
                ...(!isMobile && {
                    height: '100vh',
                    overflow: 'hidden',
                }),

                // 2. El marco principal que contiene todo. Ancla (position:relative)
                //    de la AppBar de escritorio, que va absoluta sobre la columna de
                //    contenido (ver CustomAppBar).
                '& .RaLayout-appFrame': {
                    marginTop: 0,
                    alignItems: 'flex-start',
                    ...(!isMobile && {
                        height: '100%',
                        position: 'relative',
                    }),
                },

                // Patrón A · gotcha react-admin: el sidebar se monta como Drawer
                //   docked → .RaSidebar-docked > .RaSidebar-paper > .RaSidebar-fixed
                //   > nuestro Box del rail. NO existe `.RaSidebar-root` en esta
                // versión (reglas que lo apuntaban no casaban con nada). El paper
                // trae su propio overflow/altura que recortaba el rail por abajo:
                // forzar la cadena a 100vh + overflow visible para que el rail
                // llegue de arriba abajo, a ras de los bordes.
                '& .RaSidebar-docked': {
                    margin: '0 !important',
                    padding: '0 !important',
                    ...(!isMobile && { height: '100vh' }),
                },
                '& .RaSidebar-paper': {
                    border: 'none',
                    backgroundColor: 'transparent',
                    margin: '0 !important',
                    padding: '0 !important',
                    ...(!isMobile && {
                        position: 'relative',
                        height: '100vh',
                        overflow: 'visible',
                    }),
                },
                '& .RaSidebar-fixed': {
                    margin: '0 !important',
                    ...(!isMobile && {
                        position: 'relative !important',
                        height: '100vh !important',
                        overflow: 'visible !important',
                    }),
                },

                // 3. Contenedor que agrupa Menú y Contenido. Patrón A: sin padding
                //    externo — el rail queda a ras de los bordes izquierda/arriba/
                //    abajo y la fila ocupa toda la altura del viewport (la AppBar es
                //    absoluta, así que esta fila empieza en y=0).
                '& .RaLayout-contentWithSidebar': {
                    display: 'flex',
                    flex: 1,
                    gap: 0,
                    width: '100%',
                    ...(!isMobile && {
                        padding: 0,
                        height: '100vh',
                        overflow: 'hidden',
                    }),
                },

                '& .RaLayout-content': {
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    minWidth: 0,

                    // --- LÓGICA SOLO PARA MÓVIL ---
                    ...(isMobile && {
                        height: `calc(100dvh - ${chromeHeight}px)`,
                        paddingTop: `${chromeHeight}px`,
                        paddingBottom: 0,
                        overflow: 'hidden',
                    }),

                    // --- LÓGICA SOLO PARA ESCRITORIO ---
                    // Patrón A (hybrid-3c.md §3): la AppBar absoluta mide 52px (más la
                    // barra de secciones cuando se muestra) y el padding de página
                    // (24px) se aplica UNA vez aquí, incluido el hueco superior —
                    // paddingTop = cromo + 24. Lados/abajo llevan el mismo padding.
                    ...(!isMobile && {
                        height: '100vh',
                        paddingTop: `calc(${chromeHeight}px + ${theme.spacing(3)})`,
                        paddingLeft: theme.spacing(3),
                        paddingRight: theme.spacing(3),
                        paddingBottom: theme.spacing(3),
                        boxSizing: 'border-box',
                        overflow: 'hidden',
                    }),
                },

                // 4. Lienzo de contenido — fondo igual al appbar; cada sección
                //    (header, lista, tarjetas) flota como tarjeta propia encima.
                '& .RaLayout-content > div': {
                    width: '100%',
                    backgroundColor: theme.palette.background.default,
                    WebkitOverflowScrolling: 'touch',
                    // Este elemento es la región de scroll vertical Y aloja la
                    // SectionHeader sticky de cada pantalla. Un padding-top aquí
                    // (el `p` del wrapper de página) empuja el anclaje del header
                    // `top:0` por debajo del borde del padding, abriendo una
                    // rendija entre la app bar y el header fijo por la que se
                    // cuelan las filas al desplazar. Se anula el padding superior
                    // para que el header quede pegado a la app bar; los márgenes
                    // laterales/inferior se conservan.
                    paddingTop: 0,

                    ...(!isMobile && {
                        borderRadius: 0,
                        boxShadow: 'none',
                        overflow: 'auto',
                        height: '100%',
                        // Column so a list page's [SectionHeader + List] stack can
                        // let the List fill the remaining height and scroll the table
                        // internally (Pattern A). NOTE: without rule 5 below, every
                        // other top-level block would also be a shrinkable flex item,
                        // and any of them acting as a scroll container collapses to
                        // min-height:0 instead of overflowing — the whole page then
                        // squeezes into the viewport with nothing to scroll.
                        display: 'flex',
                        flexDirection: 'column',
                        minHeight: 0,
                    }),

                    // En móvil: scroll vertical sí, horizontal no
                    ...(isMobile && {
                        flex: 1,
                        minHeight: 0,
                        borderRadius: 0,
                        boxShadow: 'none',
                        overflowY: 'auto',
                        overflowX: 'hidden',
                        maxWidth: '100vw',
                    }),
                },

                // 5. Los bloques de primer nivel de una página (SectionHeader, KPIs,
                //    WidgetCards, Paper…) son hijos flex de la región de scroll de
                //    arriba. Con flex-shrink:1 por defecto, cualquiera que sea un
                //    contenedor de scroll (overflow:hidden, como suelen ser Paper/
                //    Card de MUI) recibe min-height:0 y se encoge para caber en el
                //    viewport en vez de desbordar — la página entera "se aprieta" y
                //    nunca hay scroll (medido en /#/summary y /#/channels: clientHeight
                //    == scrollHeight). Se anula el encogimiento para que cada bloque
                //    ocupe su altura natural y sea la región la que haga scroll.
                //    Selector con especificidad cero (:where) para que una página que
                //    sí quiera ocupar la altura restante (Patrón A: flex:1+minHeight:0
                //    puesto por la propia página) gane con su propia clase.
                '& :where(.RaLayout-content > div) > *': {
                    flexShrink: 0,
                },
            }}
        >
            {props.children}
        </Layout>
        <AssistantPanel />
        <ReleaseNotesPrompt />
        </MuiLocaleProvider>
    );
};

export const CustomLayout: React.FC<LayoutProps> = (props) => {
    const location = useLocation();
    // A documentation-only person reaches the Developers section and nothing else: any other address
    // sends them back before a single screen (and its requests) is mounted.
    const redirect = documentationOnlyRedirect(location.pathname, isDocumentationOnly());
    if (redirect) return <Navigate to={redirect} replace />;
    return (
        <AnalyticsPeriodProvider>
            <AssistantProvider>
                <LayoutFrame {...props} />
            </AssistantProvider>
        </AnalyticsPeriodProvider>
    );
};

export default CustomLayout;
